/**
 * Pure geometry shared by the canvas renderer, the SVG export and the editor:
 * zigzag motion lines, freehand smoothing, approach angle, hit testing.
 */
import type { PlayData, PlayPlayer, PlayPoint } from '../types';
import { pbIsLineman } from './constants';

export interface XY {
  x: number;
  y: number;
}

/** Plugin's pbBuildZigzagPoints: teeth along a polyline, first/last on the path. */
export function buildZigzagPoints(pts: XY[], amp: number): XY[] {
  if (pts.length < 2) return pts;
  const segs: { len: number; dx: number; dy: number }[] = [];
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i]!.x - pts[i - 1]!.x,
      dy = pts[i]!.y - pts[i - 1]!.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    segs.push({ len, dx, dy });
    total += len;
  }
  const toothLen = amp * 1.4;
  const numTeeth = Math.max(3, Math.round(total / toothLen));
  const res: XY[] = [];
  for (let n = 0; n <= numTeeth; n++) {
    const dist = (n / numTeeth) * total;
    const side = n % 2 === 0 ? 1 : -1;
    let rem = dist,
      pi = 0;
    while (pi < segs.length - 1 && rem > segs[pi]!.len) {
      rem -= segs[pi]!.len;
      pi++;
    }
    const seg = segs[pi]!;
    const t = seg.len > 0 ? rem / seg.len : 0;
    const bx = pts[pi]!.x + seg.dx * t,
      by = pts[pi]!.y + seg.dy * t;
    if (n === 0 || n === numTeeth) {
      res.push({ x: bx, y: by });
      continue;
    }
    const plen = Math.sqrt(seg.dx * seg.dx + seg.dy * seg.dy) || 1;
    const px = -seg.dy / plen,
      py = seg.dx / plen;
    res.push({ x: bx + side * amp * px, y: by + side * amp * py });
  }
  return res;
}

/** Plugin's pbSmoothPoints: moving average with a stride derived from `factor`. */
export function smoothPoints(pts: PlayPoint[], factor: number): PlayPoint[] {
  if (factor === 0 || pts.length < 3) return pts;
  const step = Math.max(1, Math.floor(factor * 0.7));
  const res: PlayPoint[] = [pts[0]!];
  for (let i = step; i < pts.length - step; i += step) {
    let sx = 0,
      sy = 0,
      n = 0;
    for (let j = Math.max(0, i - step); j <= Math.min(pts.length - 1, i + step); j++) {
      sx += pts[j]!.x;
      sy += pts[j]!.y;
      n++;
    }
    res.push({ x: sx / n, y: sy / n });
  }
  res.push(pts[pts.length - 1]!);
  return res;
}

/** Angle of approach into the last point, skipping jittery final samples (>8px back). */
export function approachAngle(pts: XY[], minDist = 8): number {
  const last = pts[pts.length - 1]!;
  for (let i = pts.length - 2; i >= 0; i--) {
    const dx = last.x - pts[i]!.x,
      dy = last.y - pts[i]!.y;
    if (Math.sqrt(dx * dx + dy * dy) > minDist) return Math.atan2(dy, dx);
  }
  const prev = pts[pts.length - 2] ?? last;
  return Math.atan2(last.y - prev.y, last.x - prev.x);
}

/**
 * Plugin's pbHitPlayer. `pos` is normalized (0..1); W/H are canvas pixels;
 * scale is the editor scale. Tighter on the scratch pad, where 22 players sit
 * closer together.
 */
export function hitPlayer(
  pos: XY,
  play: PlayData | null,
  W: number,
  H: number,
  scale: number,
  scratch = false,
): PlayPlayer | null {
  if (!play) return null;
  for (let i = play.players.length - 1; i >= 0; i--) {
    const p = play.players[i]!;
    const dx = (pos.x - p.x) * W,
      dy = (pos.y - p.y) * H;
    const hitR = scratch ? 10 : pbIsLineman(p.type) ? 8 : 12;
    if (Math.sqrt(dx * dx + dy * dy) < hitR * scale) return p;
  }
  return null;
}

/** Index of the line with a point nearest `pos` within 18*scale px, or -1. */
export function hitLine(pos: XY, play: PlayData, W: number, H: number, scale: number): number {
  let best = -1,
    bestDist = 18 * scale;
  play.lines.forEach((line, li) => {
    for (const pt of line.points) {
      const dx = (pos.x - pt.x) * W,
        dy = (pos.y - pt.y) * H;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < bestDist) {
        bestDist = d;
        best = li;
      }
    }
  });
  return best;
}

export function clamp01(v: number): number {
  return Math.max(0.01, Math.min(0.99, v));
}

/** Plugin's pbPlaySignature: content hash for duplicate detection. */
export function playSignature(p: PlayData | null | undefined): string {
  if (!p) return '';
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const players = (p.players || []).map((pl) => [pl.type, pl.label || '', r(pl.x), r(pl.y), pl.ballCarrier ? 1 : 0]);
  // Animation timing is part of the content. Leaving it out would let two plays
  // that differ only in timing hash the same, and the playbook's "Clean up"
  // modal pre-ticks byte-identical copies for deletion — so an omission here
  // would quietly offer up a real, different play as a duplicate.
  const lines = (p.lines || []).map((l) => [
    l.style || '',
    (l.points || []).map((pt) => [r(pt.x), r(pt.y)]),
    l.delay ?? null,
    l.dur ?? null,
  ]);
  return JSON.stringify([(p.name || '').trim().toLowerCase(), p.cardMode || '', p.playType || '', players, lines]);
}

export interface DuplicateGroup<T extends PlayData> {
  keeper: { play: T; idx: number; sig: string };
  others: { play: T; idx: number; sig: string; identical: boolean }[];
}

/**
 * Group plays that share a name + card/play type. Exact content matches are
 * `identical` (safe to pre-tick); an edited same-name play is shown but left alone.
 */
export function findDuplicateGroups<T extends PlayData>(plays: T[], locked: (p: T) => boolean = () => false): DuplicateGroup<T>[] {
  const byName = new Map<string, { play: T; idx: number; sig: string }[]>();
  plays.forEach((p, i) => {
    if (!p || locked(p)) return;
    const k = [(p.name || '').trim().toLowerCase(), p.cardMode || '', p.playType || ''].join('|');
    if (!byName.has(k)) byName.set(k, []);
    byName.get(k)!.push({ play: p, idx: i, sig: playSignature(p) });
  });
  return [...byName.values()]
    .filter((g) => g.length > 1)
    .map((g) => {
      const keeper = g[0]!;
      return { keeper, others: g.slice(1).map((e) => ({ ...e, identical: e.sig === keeper.sig })) };
    });
}
